import React, { useEffect, useState } from 'react'
import { launchesCategory } from '../shared/jsonData'
import UseFetchPost from '../shared/useFetchPost'
import { Navigation, Pagination } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { cn } from '../shared/utils'

import 'swiper/scss'
import 'swiper/scss/navigation'
import 'swiper/scss/pagination'
import './RaffleSlider.scss'
import Button from '../Button'

function parseDate(s) {
  if (s === undefined) return
  var months = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11
  }
  var p = s.split(' ')
  var d = p[0].split('-')
  var t = p[1].split(':')

  return new Date(d[2], months[d[1].toLowerCase()], d[0], t[0], t[1], t[2])
}

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window
  return {
    width,
    height
  }
}

function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  )

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return windowDimensions
}

// Utility function to calculate time left
const calculateTimeLeft = (startdate, enddate) => {
  const now = new Date()
  const start = new Date(startdate)
  const end = new Date(enddate)

  let timeLeft = {}

  if (now < start) {
    const difference = start - now
    timeLeft = {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
      message: 'Starts in'
    }
  } else if (now >= start && now < end) {
    const difference = end - now
    timeLeft = {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
      message: 'Draw closes in'
    }
  } else {
    timeLeft = {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      message: 'Draw Closed'
    }
  }

  return timeLeft
}

const sortLaunches = (prods) => {
  const currentDate = new Date()

  // make sure all products have an enddate
  prods.map((item) => {
    if (item.enddate === undefined) item.enddate = '01-Jan-1970 00:00:00'

    return item
  })

  const liveProducts = prods.filter(
    (product) => parseDate(product.enddate) > currentDate
  )
  const expiredProducts = prods.filter(
    (product) => parseDate(product.enddate) <= currentDate
  )

  // Sort live products by enddate ascending (soonest ending first)
  liveProducts.sort((a, b) => parseDate(a.enddate) - parseDate(b.enddate))

  // Sort expired products by enddate descending (most recently expired first)
  expiredProducts.sort((a, b) => parseDate(b.enddate) - parseDate(a.enddate))

  const combinedProducts =
    liveProducts.length === 3
      ? liveProducts // if 3, show all 3
      : liveProducts.length > 3
      ? liveProducts.slice(0, 6) // if more, show all (limit to 6)
      : [...liveProducts, ...expiredProducts.slice(0, 3 - liveProducts.length)] // less than 3, show recently ended to fill (3 max)

  return combinedProducts
}

// COMPONENT
const RaffleSlider = () => {
  // DATA FETCH
  const [nqdata, setHomepageData] = useState({ cards: [] })
  const getNQLaunches = UseFetchPost(
    launchesCategory.endPointUrl,
    launchesCategory.endPointPrams
  )

  useEffect(() => {
    if (getNQLaunches && getNQLaunches.data && getNQLaunches.data.products) {
      // console.log('Fetched Data:', getNQLaunches.data.products.products)
      const content = getNQLaunches.data.products.products

      const testList = content.map((item) => {
        const product = item.product
        const startdate = product.option.startdate
        const enddate =
          item.product.attributes.launch_end_date === undefined
            ? undefined
            : item.product.attributes.launch_end_date[0]

        return { ...product, startdate, enddate }
      })
      setHomepageData({ cards: sortLaunches(testList) })
    }
  }, [getNQLaunches])

  const { width } = useWindowDimensions()

  return (
    <section className={'container pb-12'}>
      <div className="flex items-center justify-between w-full mb-6">
        <h3 className="launches-title">Launches</h3>
        <a
          href="/launches"
          className="px-5 py-3 tracking-wider text-base border border-solid border-[#dedede]"
        >
          View All{' '}
        </a>
      </div>
      <Swiper
        modules={[Navigation, Pagination]}
        navigation={width >= 768 && nqdata.cards.length > 3} // tablet & desktop get navigation buttons if more than 3 launches
        pagination={{ clickable: true }}
        loop={width < 425 || nqdata.cards.length > 3} // mobile can always loop, tablet & desktop loop if more than 3 launches
        slidesPerView={1.2}
        spaceBetween={10}
        breakpoints={{
          // when window width is >= 768px (tablet + above)
          768: {
            slidesPerView: 3,
            slidesPerGroup: 3,
            spaceBetween: 16
          }
        }}
      >
        {nqdata.cards &&
          Array.isArray(nqdata.cards) &&
          nqdata.cards.map((slide, index) => (
            <SwiperSlide key={index}>
              <LaunchProduct slide={slide} />
            </SwiperSlide>
          ))}
      </Swiper>
    </section>
  )
}

const LaunchProduct = ({ slide }) => {
  const [timeLeft, setTimeLeft] = useState(
    calculateTimeLeft(slide.startdate, slide.enddate)
  )

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(slide.startdate, slide.enddate))
    }, 1000) // Update every second

    return () => clearInterval(timer)
  }, [slide.startdate, slide.enddate])

  return (
    <a className={'launch-card flex flex-col p-0 w-full mb-0'} href={slide.url}>
      <div className="relative h-[192px] overflow-hidden lg:h-[20vw] bg-[#e6e6e6]">
        <img
          className={cn(
            '!absolute -bottom-20 left-1/2 -translate-x-1/2',
            timeLeft.message === 'Draw Closed' && 'opacity-30'
          )}
          src={slide.image}
          alt={slide.name}
        />
      </div>
      {/* Display the countdown timer */}
      <CountdownTimer timeLeft={timeLeft} />
    </a>
  )
}

// Countdown Timer Component
const CountdownTimer = ({ timeLeft }) => {
  return (
    <div className="absolute bottom-0 left-0">
      {timeLeft.message && (
        <p
          className={cn(
            '!text-xs !tracking-wider p-2 bg-white border border-[#e6e6e6] border-solid',
            timeLeft.message === 'Draw Closed' && 'font-bold'
          )}
        >
          {timeLeft.message}{' '}
          {timeLeft.message !== 'Draw Closed' && (
            <span className="font-bold">
              {`${timeLeft.days}d ${timeLeft.hours}h ${timeLeft.minutes}m ${timeLeft.seconds}s`}
            </span>
          )}
        </p>
      )}
    </div>
  )
}

export default RaffleSlider
